// breather/devPlayground.ts
//
// Dev Playground (internal tooling)
// Extracted from main.tsx to reduce bloat and keep the runtime path readable.
// Behavior should remain identical.

import gsap from 'gsap'

export function mountDevPlayground(ctx: { blob: any; lighting: any }) {
  const { blob, lighting } = ctx

  const panel = document.createElement('div')
  panel.id = 'dev-panel'
  panel.style.cssText = [
    'position:fixed',
    'top:12px',
    'right:12px',
    'width:320px',
    'z-index:9999',
    'padding:12px',
    'border-radius:14px',
    'background:rgba(16,26,47,0.78)',
    'backdrop-filter: blur(0px)',
    'box-shadow: 0 10px 30px rgba(0,0,0,0.35)',
    'color:#fff',
    'font: 12px/1.25 system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    'border: 1px solid rgba(248,243,255,0.14)',
    'max-height: calc(100vh - 24px)',
    'overflow: auto',
    'overscroll-behavior: contain',
    '-webkit-overflow-scrolling: touch',
    'padding-bottom: 14px',
  ].join(';')

  panel.innerHTML = `
    <div style="display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:10px;">
      <div style="font-weight:700; letter-spacing:0.08em; opacity:0.85;">DEV PLAYGROUND</div>
      <button id="dev-hide" style="
        appearance:none; border:0; cursor:pointer;
        background:rgba(255,255,255,0.10);
        color:#fff; padding:6px 10px; border-radius:10px;
      ">Hide</button>
    </div>

    <div style="opacity:0.75; margin-bottom:12px;">
      URL flag is active. This panel is injected only in dev mode.
    </div>

    <div id="dev-accordion" style="display:flex; flex-direction:column; gap:10px;"></div>
  `

  document.body.appendChild(panel)

  const hideBtn = panel.querySelector('#dev-hide') as HTMLButtonElement
  hideBtn.onclick = () => {
    panel.style.display = 'none'
    console.log('[dev] panel hidden, refresh to restore')
  }

  const accordion = panel.querySelector('#dev-accordion') as HTMLDivElement

  // ---- Accordion helpers ----------------------------------------------------

  const sectionStyle = [
    'border-radius:12px',
    'border: 1px solid rgba(248,243,255,0.12)',
    'background: rgba(0,0,0,0.12)',
    'overflow:hidden',
  ].join(';')

  const summaryStyle = [
    'cursor:pointer',
    'padding:10px 10px',
    'font-weight:700',
    'letter-spacing:0.06em',
    'opacity:0.90',
    'user-select:none',
    'outline:none',
    'list-style:none',
  ].join(';')

  const contentStyle = [
    'padding:10px',
    'padding-top:8px',
    'display:flex',
    'flex-direction:column',
    'gap:10px',
  ].join(';')

  const makeSection = (title: string, open = false) => {
    const details = document.createElement('details')
    details.open = open
    details.style.cssText = sectionStyle

    const summary = document.createElement('summary')
    summary.textContent = title
    summary.style.cssText = summaryStyle

    summary.innerHTML = `
      <div style="display:flex; align-items:center; justify-content:space-between; gap:10px;">
        <span>${title}</span>
        <span style="opacity:0.7; font-weight:600;">${open ? '–' : '+'}</span>
      </div>
    `
    details.appendChild(summary)

    details.addEventListener('toggle', () => {
      const marker = summary.querySelector('span:last-child') as HTMLSpanElement | null
      if (marker) marker.textContent = details.open ? '–' : '+'
    })

    const body = document.createElement('div')
    body.style.cssText = contentStyle
    details.appendChild(body)

    accordion.appendChild(details)
    return body
  }

  const secShape     = makeSection('Shape', true)
  const secBand      = makeSection('Air Band', true)
  const secWobble    = makeSection('Wobble', false)
  const secRimEnv    = makeSection('Rim / Env / Bloom', false)
  const secHighlight = makeSection('Highlight', false)
  const secMesh      = makeSection('Mesh', false)

  const addSlider = (
    container: HTMLElement,
    opts: {
      label: string
      min: number
      max: number
      step: number
      get(): number
      set(v: number): void
    }
  ) => {
    const row = document.createElement('div')
    row.style.cssText = 'display:flex; flex-direction:column; gap:6px;'

    const top = document.createElement('div')
    top.style.cssText = 'display:flex; justify-content:space-between; align-items:center; gap:8px;'

    const lab = document.createElement('div')
    lab.textContent = opts.label
    lab.style.cssText = 'font-weight:600; opacity:0.9;'

    const val = document.createElement('div')
    val.style.cssText = 'font-variant-numeric: tabular-nums; opacity:0.8;'
    val.textContent = String(opts.get().toFixed(3))

    top.appendChild(lab)
    top.appendChild(val)

    const input = document.createElement('input')
    input.type = 'range'
    input.min = String(opts.min)
    input.max = String(opts.max)
    input.step = String(opts.step)
    input.value = String(opts.get())

    input.oninput = () => {
      const v = parseFloat(input.value)
      opts.set(v)
      val.textContent = v.toFixed(3)
    }

    row.appendChild(top)
    row.appendChild(input)
    container.appendChild(row)
  }

  // ---------------------------------------
  // Simple color picker helper
  // ---------------------------------------
  const addColor = (
    section: HTMLDivElement,
    opts: {
      label: string
      get(): string
      set(v: string): void
    }
  ) => {
    const row = document.createElement('div')
    row.style.cssText = 'display:flex; flex-direction:column; gap:6px;'

    const top = document.createElement('div')
    top.style.cssText = 'display:flex; justify-content:space-between; align-items:center; gap:8px;'

    const lab = document.createElement('div')
    lab.textContent = opts.label
    lab.style.cssText = 'font-weight:600; opacity:0.9;'

    const input = document.createElement('input')
    input.type = 'color'
    input.value = opts.get()
    input.oninput = () => opts.set(input.value)

    top.appendChild(lab)
    top.appendChild(input)
    row.appendChild(top)

    section.appendChild(row)
  }

  addSlider(secShape, {
    label: 'uBulgeMax (shape strength)',
    min: 0.00, max: 0.35, step: 0.005,
    get: () => blob.uniforms.uBulgeMax.value,
    set: (v) => { blob.uniforms.uBulgeMax.value = v }
  })

  addSlider(secShape, {
    label: 'uElongate (top/bottom bias)',
    min: -1.00, max: 1.00, step: 0.01,
    get: () => blob.uniforms.uElongate.value,
    set: (v) => { blob.uniforms.uElongate.value = v }
  })

  addSlider(secBand, {
    label: 'uLightY (air band center)',
    min: 0.00, max: 1.00, step: 0.01,
    get: () => blob.uniforms.uLightY.value,
    set: (v) => { blob.uniforms.uLightY.value = v }
  })

  addSlider(secBand, {
    label: 'uAirWidth (band radius)',
    min: 0.02, max: 0.40, step: 0.005,
    get: () => blob.uniforms.uAirWidth.value,
    set: (v) => { blob.uniforms.uAirWidth.value = v }
  })

  addSlider(secBand, {
    label: 'uAirWeight (band intensity)',
    min: 0.00, max: 1.50, step: 0.01,
    get: () => blob.uniforms.uAirWeight.value,
    set: (v) => { blob.uniforms.uAirWeight.value = v }
  })

  addSlider(secWobble, {
    label: 'uIdleAmp (blobbiness)',
    min: 0.00, max: 0.08, step: 0.001,
    get: () => blob.uniforms.uIdleAmp.value,
    set: (v) => { blob.uniforms.uIdleAmp.value = v }
  })

  addSlider(secWobble, {
    label: 'uNoiseScale (wobble scale)',
    min: 0.10, max: 3.00, step: 0.01,
    get: () => blob.uniforms.uNoiseScale.value,
    set: (v) => { blob.uniforms.uNoiseScale.value = v }
  })

  addSlider(secWobble, {
    label: 'uNoiseSpeed (wobble speed)',
    min: 0.00, max: 2.00, step: 0.01,
    get: () => blob.uniforms.uNoiseSpeed.value,
    set: (v) => { blob.uniforms.uNoiseSpeed.value = v }
  })

  addSlider(secRimEnv, {
    label: 'uFresnelPower (rim tightness)',
    min: 0.50, max: 6.00, step: 0.01,
    get: () => blob.uniforms.uFresnelPower.value,
    set: (v) => { blob.uniforms.uFresnelPower.value = v }
  })

  addSlider(secRimEnv, {
    label: 'uEnvIntensity (overall lift)',
    min: 0.00, max: 3.00, step: 0.01,
    get: () => blob.uniforms.uEnvIntensity.value,
    set: (v) => { blob.uniforms.uEnvIntensity.value = v }
  })

  addSlider(secRimEnv, {
    label: 'uBloomBoost (extra bloom)',
    min: 0.00, max: 2.00, step: 0.01,
    get: () => blob.uniforms.uBloomBoost.value,
    set: (v) => { blob.uniforms.uBloomBoost.value = v }
  })

  addSlider(secHighlight, {
    label: 'Highlight intensity',
    min: 0.00, max: 1.00, step: 0.01,
    get: () => blob.uniforms.uHighlightIntensity.value,
    set: (v) => { blob.uniforms.uHighlightIntensity.value = v }
  })

  addSlider(secHighlight, {
    label: 'Highlight center X',
    min: -1.00, max: 1.00, step: 0.01,
    get: () => blob.uniforms.uHighlightCenter.value.x,
    set: (v) => { blob.uniforms.uHighlightCenter.value.x = v }
  })

  addSlider(secHighlight, {
    label: 'Highlight center Y',
    min: -1.00, max: 1.00, step: 0.01,
    get: () => blob.uniforms.uHighlightCenter.value.y,
    set: (v) => { blob.uniforms.uHighlightCenter.value.y = v }
  })

  addSlider(secHighlight, {
    label: 'Highlight radius inner (thickness)',
    min: 0.01, max: 1.00, step: 0.005,
    get: () => blob.uniforms.uHighlightRadiusInner.value,
    set: (v) => { blob.uniforms.uHighlightRadiusInner.value = v }
  })

  addSlider(secHighlight, {
    label: 'Highlight radius outer (length)',
    min: 0.01, max: 1.50, step: 0.005,
    get: () => blob.uniforms.uHighlightRadiusOuter.value,
    set: (v) => { blob.uniforms.uHighlightRadiusOuter.value = v }
  })

  addSlider(secHighlight, {
    label: 'Highlight aspect (vertical stretch)',
    min: 0.50, max: 2.50, step: 0.01,
    get: () => blob.uniforms.uHighlightAspect.value,
    set: (v) => { blob.uniforms.uHighlightAspect.value = v }
  })

  addSlider(secMesh, {
    label: 'Mesh width (X/Z)',
    min: 0.40, max: 1.20, step: 0.005,
    get: () => blob.mesh.scale.x,
    set: (v) => { blob.mesh.scale.set(v, blob.mesh.scale.y, v) }
  })

  addSlider(secMesh, {
    label: 'Mesh height (Y)',
    min: 0.40, max: 1.40, step: 0.005,
    get: () => blob.mesh.scale.y,
    set: (v) => { blob.mesh.scale.set(blob.mesh.scale.x, v, blob.mesh.scale.z) }
  })

  // -----------------------------
  // Halo controls (LightingRig)
  // -----------------------------
  const secHalo = makeSection('Halo', false)

  const halo = lighting?.halo
  const haloMat = halo?.material as any

  if (halo && haloMat) {
    addSlider(secHalo, {
      label: 'Halo opacity',
      min: 0.00, max: 0.30, step: 0.005,
      get: () => haloMat.opacity ?? 0,
      set: (v) => { haloMat.opacity = v }
    })

    addSlider(secHalo, {
      label: 'Halo scale',
      min: 0.50, max: 3.00, step: 0.01,
      get: () => halo.scale?.x ?? 1,
      set: (v) => { halo.scale.set(v, v, halo.scale.z ?? 1) }
    })

    addSlider(secHalo, {
      label: 'Halo Z (depth)',
      min: -2.00, max: 0.00, step: 0.01,
      get: () => halo.position?.z ?? -0.5,
      set: (v) => { halo.position.z = v }
    })

    addColor(secHalo, {
      label: 'Halo color',
      get: () => '#' + (haloMat.color?.getHexString?.() ?? 'ffffff'),
      set: (hex) => { haloMat.color?.set?.(hex) }
    })
  } else {
    const note = document.createElement('div')
    note.style.cssText = 'opacity:0.7; font-size:12px;'
    note.textContent = 'Halo not found on lighting rig (lighting.halo missing).'
    secHalo.appendChild(note)
  }

  console.log('[dev] playground mounted (accordion)')
}
